import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { BaseModalType } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { LivePriceCell } from '../components/LivePriceCell'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import { formatSymbol, getOpenLegTargetPrice } from '../utils/formatters'

interface CloseTradeModalProps {
  onConfirm: (onSettled: () => void) => boolean
  order: PendingOrderWithLots
  priceIncrement?: number | null
}

export const CancelOrderModal: BaseModalType<CloseTradeModalProps> = (props) => {
  const { close, onConfirm, order, priceIncrement } = props
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const fractionDigits = getStepDecimals(priceIncrement)
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleConfirm = () => {
    const isRequestSent = onConfirm(() => {
      if (isMountedRef.current) {
        setIsLoading(false)
        close()
      }
    })
    if (isRequestSent) {
      setIsLoading(true)
    }
  }

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Cancel Order')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box mb='x16'>
            <Body1 color='text-med-em'>
              {t('Portfolio.Are you sure you want to cancel this pending order')}
            </Body1>
          </Box>
          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.Asset')}</Label>
              <Value>{formatSymbol(order.instrument, order.description)}</Value>
            </Box>
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.EntryPrice')}</Label>
              <Value>
                <SensitiveData>
                  {(() => {
                    const tp = getOpenLegTargetPrice(order)
                    return tp != null
                      ? formatNumber(tp, {
                          minDecimalPlaces: fractionDigits,
                          maxDecimalPlaces: fractionDigits,
                        })
                      : '-'
                  })()}
                </SensitiveData>
              </Value>
            </Box>
            <Box flex justify='space-between' pb='x8'>
              <Label>{t('Portfolio.Current price')}</Label>

              <LivePriceCell
                trade={{ symbol: order.instrument || '', side: order.side, priceIncrement }}
                priceType='order'
              />
            </Box>
            <Box flex justify='space-between'>
              <Label>{t('Portfolio.Size')}</Label>
              <Value>
                <SensitiveData>{order.sizeLots || ''}</SensitiveData>
              </Value>
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary onClick={handleConfirm} loading={isLoading}>
          {t('Confirm')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

const Label: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-med-em'>{children}</Body2>
)

const Value: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-high-em'>{children}</Body2>
)
